import React from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../i18n';
import { legalCopy, CONTACT } from '../i18n/legal';
import useSeo from '../hooks/useSeo';
import NavBrand from '../components/NavBrand';
import UserMenu from '../components/UserMenu';
import PublicNav from '../components/PublicNav';
import BackBar from '../components/BackBar';
import { useAuth } from '../context/AuthContext';
import './InfoPages.css';
import './Dashboard.css';

// ── ABOUT / PRIVACY ──────────────────────────────────────────────────────────
// Two routes, one layout. They are the same kind of page — a title, prose, and
// the phone numbers at the bottom — and giving each its own shell would mean
// two headers to keep in step for no gain.
//
// PUBLIC. Both render for a signed-out visitor, which is the point: a privacy
// policy behind a login is not a privacy policy. The header therefore uses the
// same PublicNav a visitor gets on /designers and /shop, and switches to the
// account menu when there is a session.
//
// The copy itself lives in i18n/legal.js — see the note at the top of that file
// for what the privacy text is based on and the caveat that comes with it.

function PhoneList({ label }) {
  return (
    <div className="ifp-contact">
      {label && <p className="ifp-contact-label">{label}</p>}
      <ul className="ifp-phones">
        {CONTACT.phones.map((p, i) => (
          <li key={p}>
            {/* A real tel: link — on a phone this is the whole point, and on a
                desktop it is still the number, selectable and copyable. */}
            <a className="ifp-phone" href={`tel:${CONTACT.phonesHref[i]}`}>
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
              </svg>
              {p}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InfoShell({ children }) {
  const { user } = useAuth();
  // No usePageChrome claim: these pages use the app's own `--site-*` palette,
  // which ThemeProvider already keeps the browser chrome matching. Only pages
  // that FIX a palette regardless of theme — the landing, the auth screens —
  // need to claim it.
  return (
    <div className="dash-root">
      <header className="dash-nav">
        <NavBrand />
        <div className="dash-nav-right">
          {user ? <UserMenu /> : <PublicNav />}
        </div>
      </header>
      <BackBar width={860} fallback="/" />
      <main className="dash-main ifp-main">{children}</main>
    </div>
  );
}

export function About() {
  const { lang } = useT();
  const c = legalCopy(lang);

  useSeo({
    title: 'About Tunify — AI Home Design & 3D Interior Planner',
    description: 'What Tunify is, who builds it, and how to reach us.',
    path: '/about',
  });

  return (
    <InfoShell>
      <h1 className="ifp-title">{c.about_title}</h1>
      <p className="ifp-lead">{c.about_lead}</p>
      {c.about_body.map((p, i) => <p key={i} className="ifp-p">{p}</p>)}

      {/* ── THE AI CAVEAT ──────────────────────────────────────────────────
          Stated here in full, and again in one line inside the generator
          itself (see RealStagingModal) — the place it matters is the moment
          someone is about to trust a render, not a page they may never open. */}
      <section className="ifp-note">
        <h2 className="ifp-note-title">{c.ai_title}</h2>
        <p className="ifp-p">{c.ai_body}</p>
      </section>

      <section className="ifp-section">
        <h2 className="ifp-h2">{c.about_contact_title}</h2>
        <PhoneList label={c.about_contact_sub} />
      </section>

      <p className="ifp-crosslink">
        <Link to="/privacy">{c.privacy_title}</Link>
      </p>
    </InfoShell>
  );
}

export function Privacy() {
  const { lang } = useT();
  const c = legalCopy(lang);

  useSeo({
    title: 'Privacy Policy — Tunify',
    description: 'What Tunify collects, why, and who else processes it.',
    path: '/privacy',
  });

  return (
    <InfoShell>
      <h1 className="ifp-title">{c.privacy_title}</h1>
      <p className="ifp-lead">{c.privacy_intro}</p>

      {c.privacy_sections.map(([heading, body]) => (
        <section key={heading} className="ifp-section">
          <h2 className="ifp-h2">{heading}</h2>
          <p className="ifp-p">{body}</p>
        </section>
      ))}

      <section className="ifp-section">
        <PhoneList label={c.privacy_contact} />
      </section>

      {/* Said plainly rather than buried: this text was written from the code,
          not by a lawyer, and the reader is entitled to know which. */}
      <p className="ifp-caveat">{c.legal_reviewed_note}</p>

      <p className="ifp-crosslink">
        <Link to="/about">{c.about_title}</Link>
      </p>
    </InfoShell>
  );
}
